import clsx from "clsx";
import PropTypes from "prop-types";
import * as React from "react";
import { Meter as BaseMeter } from "@base-ui/react/meter";
import shared from "../../shared.module.css";
import css from "./Meter.module.css";

const computeLevel = ({ value, min, max, low, high, optimum }) => {
  const hasLow = low != null;
  const hasHigh = high != null;

  if (!hasLow && !hasHigh) return "high";

  const effectiveLow = hasLow ? low : min;
  const effectiveHigh = hasHigh ? high : max;

  if (optimum != null) {
    const optimumInLow = optimum <= effectiveLow;
    const optimumInHigh = optimum >= effectiveHigh;

    if (optimumInLow) {
      if (value <= effectiveLow) return "high";
      if (value <= effectiveHigh) return "medium";
      return "low";
    }

    if (optimumInHigh) {
      if (value >= effectiveHigh) return "high";
      if (value >= effectiveLow) return "medium";
      return "low";
    }
  }

  if (value < effectiveLow) return "low";
  if (value > effectiveHigh) return "low";
  if (hasLow && hasHigh && value >= effectiveLow && value <= effectiveHigh) {
    return "high";
  }
  return "medium";
};

const Meter = React.forwardRef(
  (
    {
      value,
      min = 0,
      max = 100,
      low,
      high,
      optimum,
      label,
      showValue = false,
      format,
      className,
      ...rest
    },
    ref,
  ) => {
    const level = computeLevel({ value, min, max, low, high, optimum });
    const pct = ((value - min) / (max - min)) * 100;

    const formatted =
      format && typeof Intl !== "undefined"
        ? new Intl.NumberFormat(undefined, format).format(value)
        : `${Math.round(pct)}%`;

    return (
      <BaseMeter.Root
        ref={ref}
        value={value}
        min={min}
        max={max}
        className={clsx(css.root, className)}
        {...rest}
      >
        <BaseMeter.Label className={shared.visuallyHidden}>
          {label}
        </BaseMeter.Label>
        <BaseMeter.Track className={css.track}>
          <BaseMeter.Indicator className={css.indicator} data-level={level} />
        </BaseMeter.Track>
        {showValue && <span className={css.value}>{formatted}</span>}
      </BaseMeter.Root>
    );
  },
);

Meter.displayName = "Meter";

Meter.propTypes = {
  value: PropTypes.number.isRequired,
  min: PropTypes.number,
  max: PropTypes.number,
  low: PropTypes.number,
  high: PropTypes.number,
  optimum: PropTypes.number,
  label: PropTypes.string.isRequired,
  showValue: PropTypes.bool,
  format: PropTypes.object,
  className: PropTypes.string,
};

export default Meter;
